import { useRef, useState, useMemo, useCallback } from 'react';
import {
  Modal,
  Text,
  Button,
  Stack,
  SimpleGrid,
  Switch,
} from '@mantine/core';
import { IconDownload, IconShare } from '@tabler/icons-react';
import { saveAs } from 'file-saver';
import { buildExportFilename } from '@/hooks/useExportImage';
import { apiClient } from '@/api/client';
import type { TimelineEntry } from '@/types/timeline';

interface ShareableImageGridProps {
  opened: boolean;
  onClose: () => void;
  entries: TimelineEntry[];
  organismName: string;
  category?: string | null;
  subcategory?: string | null;
  vendorName?: string | null;
}

/** Max intermediate photos shown in the filmstrip. */
const MAX_FILMSTRIP = 8;

/** Brand gradient used for the card border accent (teal → orange). */
const GRADIENT_BORDER =
  'linear-gradient(135deg, #178a82 0%, #1fbfa5 30%, #f59f00 70%, #fd7e14 100%)';

function formatGridDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Compute a human-readable time span between two dates. */
function timeSpanLabel(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 30) return `${diffDays} DAY${diffDays === 1 ? '' : 'S'}`;
  const months = Math.round(diffDays / 30.44);
  if (months < 12) return `${months} MONTH${months === 1 ? '' : 'S'}`;
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  if (remainingMonths === 0) return `${years} YEAR${years === 1 ? '' : 'S'}`;
  return `${years}Y ${remainingMonths}M`;
}

/**
 * Pick `count` items evenly spaced from `items`, always preserving order.
 * Used when there are more intermediate photos than filmstrip slots.
 */
export function sampleEvenly<T>(items: T[], count: number): T[] {
  if (count <= 0) return [];
  if (count === 1) return [items[Math.floor(items.length / 2)] as T];
  if (items.length <= count) return items;
  const result: T[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.round((i * (items.length - 1)) / (count - 1));
    result.push(items[idx] as T);
  }
  return result;
}

/**
 * Pick a column count that avoids or minimizes orphaned cells in the last row.
 */
function filmstripCols(n: number): number {
  if (n <= 3) return n;
  if (n === 4) return 4;
  if (n === 5) return 5;
  if (n === 6) return 3;
  return 4; // 7 or 8
}

/** Interpolate a teal-to-orange gradient color for a filmstrip position. */
function timelineDotColor(index: number, total: number): string {
  const colors = [
    [23, 138, 130],  // teal (primary)
    [31, 191, 165],  // teal-glow
    [245, 159, 0],   // yellow-orange
    [253, 126, 20],  // orange
  ];
  if (total <= 1) return `rgb(${colors[0]!.join(',')})`;
  const t = index / (total - 1);
  const segment = t * (colors.length - 1);
  const i = Math.floor(segment);
  const f = segment - i;
  const c1 = colors[Math.min(i, colors.length - 1)]!;
  const c2 = colors[Math.min(i + 1, colors.length - 1)]!;
  const r = Math.round(c1[0]! + f * (c2[0]! - c1[0]!));
  const g = Math.round(c1[1]! + f * (c2[1]! - c1[1]!));
  const b = Math.round(c1[2]! + f * (c2[2]! - c1[2]!));
  return `rgb(${r},${g},${b})`;
}

// ── Canvas export helpers ──────────────────────────────────────────────

/** Load a data URL string into an HTMLImageElement. */
function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/** Draw an image cropped to fill a rectangle (object-fit: cover). */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number, y: number, w: number, h: number,
) {
  const imgAr = img.naturalWidth / img.naturalHeight;
  const boxAr = w / h;
  let sx: number, sy: number, sw: number, sh: number;
  if (imgAr > boxAr) {
    sh = img.naturalHeight; sw = sh * boxAr;
    sx = (img.naturalWidth - sw) / 2; sy = 0;
  } else {
    sw = img.naturalWidth; sh = sw / boxAr;
    sx = 0; sy = (img.naturalHeight - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

/** Trace a rounded-rect path. */
function rrect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/** Draw a pill-shaped chip with text. */
function drawChip(
  ctx: CanvasRenderingContext2D,
  text: string, x: number, y: number,
  bg: string, fg: string, fontSize: number, fontWeight: string,
) {
  ctx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  const tw = ctx.measureText(text).width;
  const px = fontSize * 0.6;
  const py = fontSize * 0.35;
  const cw = tw + 2 * px;
  const ch = fontSize + 2 * py;

  ctx.fillStyle = bg;
  rrect(ctx, x, y, cw, ch, 4);
  ctx.fill();

  ctx.fillStyle = fg;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(text, x + px, y + py);
  return { w: cw, h: ch };
}

/** Photo cell for hero (first/latest) slots — preserves full aspect ratio. */
function HeroCell({
  entry,
  label,
  dateSide,
}: {
  entry: TimelineEntry;
  label: 'FIRST' | 'LATEST';
  dateSide: 'left' | 'right';
}) {
  const src = entry.photo_display_url ?? entry.photo_url;
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #dce4ec',
      }}
    >
      <img
        src={src ?? ''}
        alt={`${label.toLowerCase()} photo — ${formatGridDate(entry.entry_date)}`}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
        }}
      />
      {/* FIRST / LATEST chip */}
      <div
        style={{
          position: 'absolute',
          top: 8,
          ...(dateSide === 'left' ? { left: 8 } : { right: 8 }),
          background: 'rgba(23, 138, 130, 0.82)',
          color: '#d8f5ee',
          fontSize: 8,
          fontWeight: 700,
          padding: '3px 8px',
          borderRadius: 4,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.07em',
        }}
      >
        {label}
      </div>
      {/* Date chip */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          ...(dateSide === 'left' ? { left: 8 } : { right: 8 }),
          background: 'rgba(0, 0, 0, 0.52)',
          color: '#ffffff',
          fontSize: 10,
          fontWeight: 500,
          padding: '3px 8px',
          borderRadius: 4,
        }}
      >
        {formatGridDate(entry.entry_date)}
      </div>
    </div>
  );
}

/** Photo cell for the intermediate filmstrip — preserves aspect ratio. */
function FilmstripCell({
  entry,
  index,
  total,
}: {
  entry: TimelineEntry;
  index: number;
  total: number;
}) {
  const src =
    entry.photo_thumb_url ?? entry.photo_display_url ?? entry.photo_url;
  const accentColor = timelineDotColor(index, total);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Photo container — fixed aspect ratio, no white gaps */}
      <div
        style={{
          position: 'relative',
          borderRadius: '6px 6px 0 0',
          overflow: 'hidden',
          border: '1px solid #e2eaf0',
          borderBottom: 'none',
          aspectRatio: '4 / 3',
        }}
      >
        <img
          src={src ?? ''}
          alt={formatGridDate(entry.entry_date)}
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
      {/* Date label below the image */}
      <div
        style={{
          background: accentColor,
          color: '#ffffff',
          fontSize: 9,
          fontWeight: 600,
          textAlign: 'center' as const,
          padding: '3px 4px',
          borderRadius: '0 0 6px 6px',
        }}
      >
        {formatGridDate(entry.entry_date)}
      </div>
    </div>
  );
}

/** Single photo display (no bookend layout needed) — preserves full aspect ratio. */
function SinglePhotoCell({ entry }: { entry: TimelineEntry }) {
  const src = entry.photo_display_url ?? entry.photo_url;
  return (
    <div
      style={{
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        border: '1px solid #dce4ec',
      }}
    >
      <img
        src={src ?? ''}
        alt={formatGridDate(entry.entry_date)}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          background: 'rgba(0, 0, 0, 0.52)',
          color: '#ffffff',
          fontSize: 10,
          fontWeight: 500,
          padding: '3px 8px',
          borderRadius: 4,
        }}
      >
        {formatGridDate(entry.entry_date)}
      </div>
    </div>
  );
}

export function ShareableImageGrid({
  opened,
  onClose,
  entries,
  organismName,
  category,
  subcategory,
  vendorName,
}: ShareableImageGridProps) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showVendor, setShowVendor] = useState(false);

  // Filter to entries with photos, sorted chronologically (oldest first)
  const photoEntries = useMemo(
    () =>
      entries
        .filter((e) => e.photo_display_url || e.photo_url)
        .sort(
          (a, b) =>
            new Date(a.entry_date).getTime() - new Date(b.entry_date).getTime(),
        ),
    [entries],
  );

  // Split into heroes (first + last) and filmstrip (middle photos)
  const { first, last, filmstrip } = useMemo(() => {
    if (photoEntries.length < 2)
      return { first: photoEntries[0] ?? null, last: null, filmstrip: [] };

    const f = photoEntries[0];
    const l = photoEntries[photoEntries.length - 1];
    const middle = photoEntries.slice(1, -1);
    return {
      first: f,
      last: l,
      filmstrip: sampleEvenly(middle, MAX_FILMSTRIP),
    };
  }, [photoEntries]);

  // Time span badge between first and last
  const spanLabel =
    first && last ? timeSpanLabel(first.entry_date, last.entry_date) : null;

  /** Send a debug log entry to the backend so it's visible in Dozzle. */
  const debugLog = useCallback(async (step: string, detail = '') => {
    try {
      await apiClient('/api/debug-log', {
        method: 'POST',
        body: JSON.stringify({ step, detail }),
      });
    } catch {
      // Best-effort — don't block export if logging fails
    }
  }, []);

  /** Convert an image src to a data URL via our backend proxy.
   *  Direct cross-origin fetch to Supabase Storage signed URLs hangs on
   *  mobile Safari, so we route through /api/image-proxy which is same-origin. */
  const toDataUrl = useCallback(async (src: string): Promise<string | null> => {
    if (!src) return null;
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15_000);
      const proxyUrl = `/api/image-proxy?url=${encodeURIComponent(src)}`;
      const resp = await apiClient(proxyUrl, { signal: controller.signal });
      clearTimeout(timeout);
      if (!resp.ok) return null;
      const blob = await resp.blob();
      return await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  }, []);

  /** Render the grid to a PNG blob using direct Canvas 2D API.
   *  html2canvas hangs on mobile Safari, so we draw everything manually. */
  const renderToBlob = useCallback(async (): Promise<Blob | null> => {
    await debugLog('renderToBlob:start', 'canvas-2d');

    // ── 1. Collect & fetch images via proxy ──────────────────────────
    const heroFirstSrc = first ? (first.photo_display_url ?? first.photo_url) : null;
    const heroLastSrc = last ? (last.photo_display_url ?? last.photo_url) : null;
    const filmSrcs = filmstrip.map(
      (e) => e.photo_thumb_url ?? e.photo_display_url ?? e.photo_url,
    );
    const allSrcs = [heroFirstSrc, heroLastSrc, ...filmSrcs].filter(Boolean) as string[];

    await debugLog('renderToBlob:fetching', `${allSrcs.length} images`);
    const imgMap = new Map<string, HTMLImageElement>();
    for (const src of allSrcs) {
      try {
        const du = await toDataUrl(src);
        if (du) imgMap.set(src, await loadImg(du));
      } catch { /* skip */ }
    }
    await debugLog('renderToBlob:loaded', `${imgMap.size}/${allSrcs.length}`);

    // ── 2. Layout constants (all in canvas pixels at 2× scale) ───────
    const S = 2;
    const EXPORT_W = 600 * S;
    const BORDER = 3 * S;
    const PAD = 20 * S;
    const GAP = 10 * S;
    const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

    const innerW = EXPORT_W - 2 * BORDER;
    const contentW = innerW - 2 * PAD;

    // ── 3. Calculate dynamic height ──────────────────────────────────
    let y = BORDER + PAD; // running y cursor

    // Header
    const nameSize = 18 * S;
    const subSize = 13 * S;
    const hasSubtitle = !!(category || subcategory);
    y += nameSize + (hasSubtitle ? 4 + subSize : 0);
    y += 16 * S; // gap after header

    // Hero images
    let heroH = 0;
    const isSingle = photoEntries.length === 1;
    if (isSingle && heroFirstSrc) {
      const img = imgMap.get(heroFirstSrc);
      const ar = img ? img.naturalWidth / img.naturalHeight : 4 / 3;
      heroH = contentW / ar;
      y += heroH;
    } else if (heroFirstSrc && heroLastSrc) {
      const heroW = (contentW - GAP) / 2;
      const img1 = imgMap.get(heroFirstSrc);
      const img2 = imgMap.get(heroLastSrc);
      const ar1 = img1 ? img1.naturalWidth / img1.naturalHeight : 4 / 3;
      const ar2 = img2 ? img2.naturalWidth / img2.naturalHeight : 4 / 3;
      heroH = Math.max(heroW / ar1, heroW / ar2);
      y += heroH;
    }

    // Filmstrip
    const filmGap = 7 * S;
    const dateLabelH = 22 * S;
    let filmRows = 0;
    let filmCellW = 0;
    let filmCellImgH = 0;
    let filmCols = 0;
    if (filmstrip.length > 0) {
      y += 12 * S; // gap before timeline connector
      y += 20 * S; // timeline connector
      y += 4 * S;  // gap after connector
      filmCols = filmstripCols(filmstrip.length);
      filmCellW = (contentW - (filmCols - 1) * filmGap) / filmCols;
      filmCellImgH = filmCellW * 3 / 4;
      filmRows = Math.ceil(filmstrip.length / filmCols);
      y += filmRows * (filmCellImgH + dateLabelH) + (filmRows - 1) * filmGap;
    }

    // Branding
    y += 12 * S; // gap before branding
    y += 16 * S; // branding line height
    y += PAD + BORDER; // bottom padding + border

    const totalH = Math.ceil(y);

    // ── 4. Create canvas and draw ────────────────────────────────────
    const canvas = document.createElement('canvas');
    canvas.width = EXPORT_W;
    canvas.height = totalH;
    const ctx = canvas.getContext('2d')!;

    // Gradient border background
    const grad = ctx.createLinearGradient(0, 0, EXPORT_W, totalH);
    grad.addColorStop(0, '#178a82');
    grad.addColorStop(0.3, '#1fbfa5');
    grad.addColorStop(0.7, '#f59f00');
    grad.addColorStop(1, '#fd7e14');
    rrect(ctx, 0, 0, EXPORT_W, totalH, 10 * S);
    ctx.fillStyle = grad;
    ctx.fill();

    // White inner card
    rrect(ctx, BORDER, BORDER, innerW, totalH - 2 * BORDER, 8 * S);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    // ── Draw header ──────────────────────────────────────────────────
    let cy = BORDER + PAD;
    ctx.fillStyle = '#1a1b1e';
    ctx.font = `700 ${nameSize}px ${FONT}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(organismName, BORDER + PAD, cy);

    // Time span badge (top-right)
    if (spanLabel) {
      const badgeFontSize = 10 * S;
      ctx.font = `700 ${badgeFontSize}px ${FONT}`;
      const badgeTw = ctx.measureText(spanLabel).width;
      const badgePx = 10 * S;
      const badgePy = 3 * S;
      const badgeW = badgeTw + 2 * badgePx;
      const badgeH = badgeFontSize + 2 * badgePy;
      const badgeX = BORDER + PAD + contentW - badgeW;
      const badgeY = cy;
      const badgeR = Math.min(badgeH / 2, badgeW / 2); // pill shape, clamped
      rrect(ctx, badgeX, badgeY, badgeW, badgeH, badgeR);
      ctx.fillStyle = 'rgba(23,138,130,0.12)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(23,138,130,0.30)';
      ctx.lineWidth = 1 * S;
      ctx.stroke();
      ctx.fillStyle = '#178a82';
      ctx.fillText(spanLabel, badgeX + badgePx, badgeY + badgePy);
    }

    cy += nameSize;
    if (hasSubtitle) {
      cy += 4;
      ctx.fillStyle = '#868e96';
      ctx.font = `400 ${subSize}px ${FONT}`;
      const subText = [category, subcategory].filter(Boolean).join(' · ');
      ctx.fillText(subText, BORDER + PAD, cy);
      cy += subSize;
    }
    if (showVendor && vendorName) {
      ctx.fillStyle = '#868e96';
      ctx.font = `400 ${subSize}px ${FONT}`;
      const vendorTw = ctx.measureText(`Bought from ${vendorName}`).width;
      ctx.fillText(`Bought from ${vendorName}`, BORDER + PAD + contentW - vendorTw, cy - subSize);
    }
    cy += 16 * S;

    // ── Draw hero images ─────────────────────────────────────────────
    const contentX = BORDER + PAD;

    if (isSingle && heroFirstSrc) {
      const img = imgMap.get(heroFirstSrc);
      if (img) {
        const ar = img.naturalWidth / img.naturalHeight;
        const h = contentW / ar;
        ctx.save();
        rrect(ctx, contentX, cy, contentW, h, 8 * S);
        ctx.clip();
        ctx.drawImage(img, contentX, cy, contentW, h);
        ctx.restore();
        // Border
        ctx.strokeStyle = '#dce4ec';
        ctx.lineWidth = 1 * S;
        rrect(ctx, contentX, cy, contentW, h, 8 * S);
        ctx.stroke();
        // Date chip
        drawChip(ctx, formatGridDate(first!.entry_date),
          contentX + 8 * S, cy + h - 8 * S - 18 * S,
          'rgba(0,0,0,0.52)', '#ffffff', 10 * S, '500');
        cy += h;
      }
    } else if (heroFirstSrc && heroLastSrc) {
      const heroW = (contentW - GAP) / 2;
      const drawHero = (
        src: string, entry: TimelineEntry, x: number,
        label: string, chipSide: 'left' | 'right',
      ) => {
        const img = imgMap.get(src);
        if (!img) return;
        const ar = img.naturalWidth / img.naturalHeight;
        const h = heroW / ar;
        ctx.save();
        rrect(ctx, x, cy, heroW, h, 8 * S);
        ctx.clip();
        ctx.drawImage(img, x, cy, heroW, h);
        ctx.restore();
        ctx.strokeStyle = '#dce4ec';
        ctx.lineWidth = 1 * S;
        rrect(ctx, x, cy, heroW, h, 8 * S);
        ctx.stroke();
        // Label chip (top)
        const chipX = chipSide === 'left' ? x + 8 * S : x + heroW - 8 * S;
        drawChip(ctx, label,
          chipSide === 'left' ? chipX : chipX - 60 * S,
          cy + 8 * S,
          'rgba(23,138,130,0.82)', '#d8f5ee', 8 * S, '700');
        // Date chip (bottom)
        const dateText = formatGridDate(entry.entry_date);
        ctx.font = `500 ${10 * S}px ${FONT}`;
        const dtw = ctx.measureText(dateText).width;
        const dateChipW = dtw + 2 * 8 * S;
        const dateX = chipSide === 'left' ? x + 8 * S : x + heroW - 8 * S - dateChipW;
        drawChip(ctx, dateText, dateX, cy + h - 8 * S - 18 * S,
          'rgba(0,0,0,0.52)', '#ffffff', 10 * S, '500');
      };
      drawHero(heroFirstSrc, first!, contentX, 'FIRST', 'left');
      drawHero(heroLastSrc, last!, contentX + heroW + GAP, 'LATEST', 'right');

      const img1 = imgMap.get(heroFirstSrc);
      const img2 = imgMap.get(heroLastSrc);
      const ar1 = img1 ? img1.naturalWidth / img1.naturalHeight : 4 / 3;
      const ar2 = img2 ? img2.naturalWidth / img2.naturalHeight : 4 / 3;
      cy += Math.max(heroW / ar1, heroW / ar2);
    }

    // ── Draw timeline connector & filmstrip ───────────────────────────
    if (filmstrip.length > 0) {
      cy += 12 * S;

      // Simplified timeline: gradient line with dots
      const lineY = cy + 10 * S;
      const lineX1 = contentX + 8 * S;
      const lineX2 = contentX + contentW - 8 * S;
      const lineGrad = ctx.createLinearGradient(lineX1, 0, lineX2, 0);
      lineGrad.addColorStop(0, timelineDotColor(0, filmstrip.length));
      lineGrad.addColorStop(1, timelineDotColor(filmstrip.length - 1, filmstrip.length));
      ctx.strokeStyle = lineGrad;
      ctx.lineWidth = 2 * S;
      ctx.beginPath();
      ctx.moveTo(lineX1, lineY);
      ctx.lineTo(lineX2, lineY);
      ctx.stroke();

      // Dots along the line
      for (let i = 0; i < filmstrip.length; i++) {
        const t = filmstrip.length > 1 ? i / (filmstrip.length - 1) : 0.5;
        const dx = lineX1 + t * (lineX2 - lineX1);
        ctx.fillStyle = timelineDotColor(i, filmstrip.length);
        ctx.beginPath();
        ctx.arc(dx, lineY, 3 * S, 0, Math.PI * 2);
        ctx.fill();
      }

      cy += 20 * S + 4 * S;

      // Filmstrip grid
      for (let i = 0; i < filmstrip.length; i++) {
        const col = i % filmCols;
        const row = Math.floor(i / filmCols);
        const cx = contentX + col * (filmCellW + filmGap);
        const cellY = cy + row * (filmCellImgH + dateLabelH + filmGap);

        const entry = filmstrip[i]!;
        const src = entry.photo_thumb_url ?? entry.photo_display_url ?? entry.photo_url;
        const img = src ? imgMap.get(src) : null;

        // Image with cover crop, rounded top
        ctx.save();
        rrect(ctx, cx, cellY, filmCellW, filmCellImgH, 6 * S);
        // Only round top corners
        ctx.beginPath();
        ctx.moveTo(cx + 6 * S, cellY);
        ctx.lineTo(cx + filmCellW - 6 * S, cellY);
        ctx.arcTo(cx + filmCellW, cellY, cx + filmCellW, cellY + 6 * S, 6 * S);
        ctx.lineTo(cx + filmCellW, cellY + filmCellImgH);
        ctx.lineTo(cx, cellY + filmCellImgH);
        ctx.lineTo(cx, cellY + 6 * S);
        ctx.arcTo(cx, cellY, cx + 6 * S, cellY, 6 * S);
        ctx.closePath();
        ctx.clip();
        if (img) {
          drawCover(ctx, img, cx, cellY, filmCellW, filmCellImgH);
        } else {
          ctx.fillStyle = '#e2eaf0';
          ctx.fillRect(cx, cellY, filmCellW, filmCellImgH);
        }
        ctx.restore();

        // Border on image
        ctx.strokeStyle = '#e2eaf0';
        ctx.lineWidth = 1 * S;
        ctx.beginPath();
        ctx.moveTo(cx + 6 * S, cellY);
        ctx.lineTo(cx + filmCellW - 6 * S, cellY);
        ctx.arcTo(cx + filmCellW, cellY, cx + filmCellW, cellY + 6 * S, 6 * S);
        ctx.lineTo(cx + filmCellW, cellY + filmCellImgH);
        ctx.lineTo(cx, cellY + filmCellImgH);
        ctx.lineTo(cx, cellY + 6 * S);
        ctx.arcTo(cx, cellY, cx + 6 * S, cellY, 6 * S);
        ctx.closePath();
        ctx.stroke();

        // Date label bar (bottom-rounded)
        const accentColor = timelineDotColor(i, filmstrip.length);
        ctx.fillStyle = accentColor;
        ctx.beginPath();
        const lbY = cellY + filmCellImgH;
        ctx.moveTo(cx, lbY);
        ctx.lineTo(cx + filmCellW, lbY);
        ctx.lineTo(cx + filmCellW, lbY + dateLabelH - 6 * S);
        ctx.arcTo(cx + filmCellW, lbY + dateLabelH, cx + filmCellW - 6 * S, lbY + dateLabelH, 6 * S);
        ctx.lineTo(cx + 6 * S, lbY + dateLabelH);
        ctx.arcTo(cx, lbY + dateLabelH, cx, lbY + dateLabelH - 6 * S, 6 * S);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.font = `600 ${9 * S}px ${FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(formatGridDate(entry.entry_date), cx + filmCellW / 2, lbY + dateLabelH / 2);
      }

      cy += filmRows * (filmCellImgH + dateLabelH) + (filmRows - 1) * filmGap;
    }

    // ── Branding ─────────────────────────────────────────────────────
    cy += 12 * S;
    const brandFontSize = 11 * S;
    ctx.font = `400 ${brandFontSize}px ${FONT}`;
    const brandText = 'Made with ReefDataHub.com';
    const brandTw = ctx.measureText(brandText).width;
    const brandX = contentX + contentW - brandTw;
    ctx.fillStyle = '#178a82';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(brandText, brandX, cy);

    await debugLog('renderToBlob:canvas-done', `${canvas.width}x${canvas.height}`);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/png');
    });
  }, [first, last, filmstrip, photoEntries, organismName, category, subcategory,
      vendorName, showVendor, spanLabel, toDataUrl, debugLog]);

  const handleDownload = useCallback(async () => {
    setIsExporting(true);
    await debugLog('handleDownload:start');
    try {
      const blob = await renderToBlob();
      if (blob) {
        await debugLog('handleDownload:saving', `blob size=${blob.size}`);
        saveAs(blob, buildExportFilename(`${organismName} photo grid`));
        await debugLog('handleDownload:done');
      } else {
        await debugLog('handleDownload:no-blob', 'renderToBlob returned null');
      }
    } catch (error) {
      await debugLog('handleDownload:error', String(error));
      console.error('Failed to export photo grid:', error);
    } finally {
      setIsExporting(false);
    }
  }, [organismName, renderToBlob, debugLog]);

  const canShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleShare = useCallback(async () => {
    setIsExporting(true);
    await debugLog('handleShare:start');
    try {
      const blob = await renderToBlob();
      if (!blob) {
        await debugLog('handleShare:no-blob', 'renderToBlob returned null');
        return;
      }
      await debugLog('handleShare:sharing', `blob size=${blob.size}`);
      const filename = buildExportFilename(`${organismName} photo grid`);
      const file = new File([blob], filename, { type: 'image/png' });

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        // Fallback to download if file sharing not supported
        saveAs(blob, filename);
      }
    } catch (error) {
      // User cancelled the share sheet — not an error
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to share photo grid:', error);
      }
    } finally {
      setIsExporting(false);
    }
  }, [organismName, renderToBlob, debugLog]);

  // Single photo — no bookend layout needed
  const isSinglePhoto = photoEntries.length === 1;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Share ${organismName} Photos`}
      size="xl"
      centered
    >
      <Stack gap="md">
        {photoEntries.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No photos to include in the grid.
          </Text>
        ) : (
          <>
            {/* Capturable grid — uses hardcoded colours so the exported PNG
                looks correct regardless of the user's colour scheme. */}
            <div
              ref={gridRef}
              style={{
                background: GRADIENT_BORDER,
                borderRadius: 10,
                padding: 3,
              }}
            >
              <div
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: 8,
                  padding: 20,
                }}
              >
                {/* Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: '#1a1b1e',
                        fontSize: 18,
                        fontWeight: 700,
                        lineHeight: 1.3,
                      }}
                    >
                      {organismName}
                    </div>
                    {(category || subcategory) && (
                      <div
                        style={{
                          color: '#868e96',
                          fontSize: 13,
                          marginTop: 2,
                        }}
                      >
                        {[category, subcategory].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {showVendor && vendorName && (
                      <div style={{ color: '#868e96', fontSize: 13 }}>
                        Bought from {vendorName}
                      </div>
                    )}
                    {/* Time span badge */}
                    {spanLabel && (
                      <div
                        style={{
                          background:
                            'linear-gradient(135deg, rgba(23,138,130,0.12), rgba(253,126,20,0.12))',
                          border: '1px solid rgba(23, 138, 130, 0.30)',
                          borderRadius: 12,
                          padding: '3px 10px',
                          fontSize: 10,
                          fontWeight: 700,
                          color: '#178a82',
                          letterSpacing: '0.06em',
                          whiteSpace: 'nowrap' as const,
                        }}
                      >
                        {spanLabel}
                      </div>
                    )}
                  </div>
                </div>

                {isSinglePhoto && first ? (
                  <SinglePhotoCell entry={first} />
                ) : first && last ? (
                  <>
                    {/* Hero row: first + last side by side */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        alignItems: 'start',
                        gap: 10,
                        marginBottom: 12,
                      }}
                    >
                      <HeroCell entry={first} label="FIRST" dateSide="left" />
                      <HeroCell entry={last} label="LATEST" dateSide="right" />
                    </div>

                    {/* Filmstrip: intermediate photos */}
                    {filmstrip.length > 0 && (
                      <>
                        {/* Arrows: teal down-arrow (left) + orange up-arrow (right) */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            paddingLeft: 16,
                            paddingRight: 16,
                            marginBottom: 4,
                          }}
                        >
                          {/* Down arrow from FIRST */}
                          <div
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderTop: `7px solid ${timelineDotColor(0, filmstrip.length)}`,
                            }}
                          />
                          {/* Up arrow toward LATEST */}
                          <div
                            style={{
                              width: 0,
                              height: 0,
                              borderLeft: '5px solid transparent',
                              borderRight: '5px solid transparent',
                              borderBottom: `7px solid ${timelineDotColor(filmstrip.length - 1, filmstrip.length)}`,
                            }}
                          />
                        </div>

                        {/* Timeline connector with coloured dots */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            margin: '0 3px 12px',
                            gap: 0,
                          }}
                        >
                          {/* Start dot */}
                          <div
                            style={{
                              width: 7,
                              height: 7,
                              borderRadius: '50%',
                              background: timelineDotColor(0, filmstrip.length),
                              flexShrink: 0,
                            }}
                          />
                          {/* Gradient line segments with intermediate dots */}
                          {filmstrip.map((_, i) => {
                            const color = timelineDotColor(i, filmstrip.length);
                            const nextColor = timelineDotColor(
                              Math.min(i + 1, filmstrip.length - 1),
                              filmstrip.length,
                            );
                            return (
                              <div
                                key={i}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flex: 1,
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: 1.5,
                                    background: `linear-gradient(to right, ${color}, ${nextColor})`,
                                  }}
                                />
                                {i < filmstrip.length - 1 && (
                                  <div
                                    style={{
                                      width: 5,
                                      height: 5,
                                      borderRadius: '50%',
                                      background: nextColor,
                                      flexShrink: 0,
                                    }}
                                  />
                                )}
                              </div>
                            );
                          })}
                          {/* End arrowhead */}
                          <div
                            style={{
                              width: 0,
                              height: 0,
                              borderTop: '5px solid transparent',
                              borderBottom: '5px solid transparent',
                              borderLeft: `8px solid ${timelineDotColor(filmstrip.length - 1, filmstrip.length)}`,
                              flexShrink: 0,
                            }}
                          />
                        </div>

                        {/* Filmstrip grid */}
                        {(() => {
                          const cols = filmstripCols(filmstrip.length);
                          const ghostCount =
                            filmstrip.length % cols === 0
                              ? 0
                              : cols - (filmstrip.length % cols);
                          return (
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                gap: 7,
                              }}
                            >
                              {filmstrip.map((entry, i) => (
                                <FilmstripCell
                                  key={entry.id}
                                  entry={entry}
                                  index={i}
                                  total={filmstrip.length}
                                />
                              ))}
                              {Array.from({ length: ghostCount }).map((_, i) => (
                                <div
                                  key={`ghost-${i}`}
                                  style={{
                                    aspectRatio: '1',
                                    borderRadius: 6,
                                    border: '1.5px dashed #e0e8f0',
                                  }}
                                />
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </>
                ) : null}

                {/* Branding */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginTop: 12,
                    gap: 5,
                  }}
                >
                  {/* Small wave icon */}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ opacity: 0.7 }}
                  >
                    <path
                      d="M2 12c1.5-2 3.5-3 5.5-1s3.5 1 5.5-1 3.5-3 5.5-1 3.5 1 5.5-1"
                      stroke="#178a82"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                    <path
                      d="M2 16c1.5-2 3.5-3 5.5-1s3.5 1 5.5-1 3.5-3 5.5-1 3.5 1 5.5-1"
                      stroke="#178a82"
                      strokeWidth="2"
                      strokeLinecap="round"
                      opacity="0.5"
                    />
                  </svg>
                  <span
                    style={{
                      color: '#178a82',
                      fontSize: 11,
                    }}
                  >
                    Made with ReefDataHub.com
                  </span>
                </div>
              </div>
            </div>

            {vendorName && (
              <Switch
                label="Include vendor name"
                checked={showVendor}
                onChange={(e) => setShowVendor(e.currentTarget.checked)}
              />
            )}

            {canShare ? (
              <SimpleGrid cols={2} spacing="sm">
                <Button
                  onClick={handleDownload}
                  loading={isExporting}
                  leftSection={<IconDownload size={16} />}
                  variant="default"
                  fullWidth
                >
                  Save
                </Button>
                <Button
                  onClick={handleShare}
                  loading={isExporting}
                  leftSection={<IconShare size={16} />}
                  fullWidth
                >
                  Share
                </Button>
              </SimpleGrid>
            ) : (
              <Button
                onClick={handleDownload}
                loading={isExporting}
                leftSection={<IconDownload size={16} />}
                fullWidth
              >
                Save Image
              </Button>
            )}
          </>
        )}
      </Stack>
    </Modal>
  );
}
